import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import MedicoCard from '../../components/public/MedicoCard';
import PublicSEO from '../../components/public/PublicSEO';
import { getBrancaSlug, getMedicoSlug } from '../../utils/slug';
import { publicPath } from '../../utils/canonicalUrl';
import { getSnapApiData } from '../../utils/snapStore';
import {
  buildBreadcrumb,
  buildMedicalSpecialty,
  buildMediciItemList,
} from '../../utils/jsonLd';
import './MediciPage.css';
import './DisciplineDetailPage.css';

function getBrancaSnapBySlug(slug) {
  if (!slug) return { branca: null, medici: [] };
  const list = getSnapApiData('/public/branche');
  if (!Array.isArray(list)) return { branca: null, medici: [] };
  const found = list.find((b) => getBrancaSlug(b) === slug);
  if (!found) return { branca: null, medici: [] };
  const brancaSnap = {
    id: found.id,
    branca: (found.branca || '').replace(/^./, (c) => c.toUpperCase()),
    descrizione: found.descrizione ?? '',
    intro: found.intro ?? '',
  };
  const mediciSnap = getSnapApiData(`/public/branche/${found.id}/medici`);
  return {
    branca: brancaSnap,
    medici: Array.isArray(mediciSnap) ? mediciSnap : [],
  };
}

const DisciplineDetailPage = () => {
  const { slug } = useParams();

  const snapInitial = getBrancaSnapBySlug(slug);
  const [branca, setBranca] = useState(snapInitial.branca);
  const [medici, setMedici] = useState(snapInitial.medici);
  const [loading, setLoading] = useState(!snapInitial.branca);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const hasSnap = !!getBrancaSnapBySlug(slug).branca;
    const load = async () => {
      if (!hasSnap) setLoading(true);
      setError(null);
      try {
        const fetchBranche = () => api.get('/public/branche');
        let resBranche;
        try {
          resBranche = await fetchBranche();
        } catch {
          await new Promise((r) => setTimeout(r, 400));
          if (cancelled) return;
          resBranche = await fetchBranche();
        }
        if (cancelled) return;
        const list = Array.isArray(resBranche.data) ? resBranche.data : [];
        const found = list.find(b => getBrancaSlug(b) === slug);
        if (!found) {
          setBranca(null);
          setMedici([]);
          setLoading(false);
          return;
        }
        setBranca({
          id: found.id,
          branca: (found.branca || '').replace(/^./, c => c.toUpperCase()),
          descrizione: found.descrizione ?? '',
          intro: found.intro ?? '',
        });
        try {
          const resMedici = await api.get(`/public/branche/${found.id}/medici`);
          if (!cancelled) setMedici(Array.isArray(resMedici.data) ? resMedici.data : []);
        } catch {
          // tieni medici da snap se presenti
        }
      } catch (err) {
        console.error('Errore caricamento disciplina:', err);
        if (!cancelled) {
          setBranca((prev) => {
            if (!prev) {
              setError(err.response?.data?.message || 'Impossibile caricare la disciplina.');
            }
            return prev;
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading && !branca) {
    return (
      <div className="discipline-detail-page">
        <PublicSEO title="Disciplina" canonicalPath={`/discipline/${slug}`} noIndex />
        <div className="container discipline-detail-loading-shell" aria-busy="true" aria-label="Caricamento" />
      </div>
    );
  }

  if (error || (!branca && !loading)) {
    return (
      <div className="discipline-detail-page">
        <PublicSEO title="Disciplina non trovata" canonicalPath="/discipline" noIndex />
        <div className="container discipline-detail-container">
          <p className="discipline-detail-error">{error || 'Disciplina non trovata.'}</p>
          <Link to={publicPath('/discipline')} className="vc-btn vc-btn-primary">Torna alle discipline</Link>
        </div>
      </div>
    );
  }

  const nomeDisciplina = branca.branca;
  const title = `${nomeDisciplina} - Medici e specialisti | Virtual Clinic`;
  const description = `Trova medici e specialisti in ${nomeDisciplina} per visite online. Prenota una videoconsulenza con un professionista certificato su Virtual Clinic.`;

  const jsonLd = [
    buildMedicalSpecialty(branca, { canonicalPath: `/discipline/${slug}`, nome: nomeDisciplina }),
    buildBreadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Discipline', path: '/discipline' },
      { name: nomeDisciplina },
    ]),
    ...(medici.length > 0 ? [buildMediciItemList(medici, getMedicoSlug)] : []),
  ];

  return (
    <div className="discipline-detail-page">
      <PublicSEO
        title={title}
        description={description}
        canonicalPath={`/discipline/${slug}`}
        keywords={`${nomeDisciplina}, specialista ${nomeDisciplina}, medico ${nomeDisciplina} online, telemedicina ${nomeDisciplina}`}
        ogImageAlt={`Specialisti in ${nomeDisciplina} su Virtual Clinic`}
        jsonLd={jsonLd}
        noIndex={medici.length === 0}
      />
      <div className="container discipline-detail-container">
        <nav className="discipline-detail-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span className="discipline-detail-breadcrumb-sep" aria-hidden>/</span>
          <Link to={publicPath('/discipline')}>Discipline</Link>
          <span className="discipline-detail-breadcrumb-sep" aria-hidden>/</span>
          <span className="discipline-detail-breadcrumb-current">{nomeDisciplina}</span>
        </nav>

        <header className="discipline-detail-header">
          <h1 className="discipline-detail-title">{nomeDisciplina}</h1>
          {branca.intro && <p className="discipline-detail-intro">{branca.intro}</p>}
          {branca.descrizione && (
            <div
              className="discipline-detail-desc"
              dangerouslySetInnerHTML={{ __html: branca.descrizione }}
            />
          )}
        </header>

        <section className="discipline-detail-medici" aria-labelledby="medici-heading">
          <h2 id="medici-heading" className="discipline-detail-medici-title">
            Medici di questa disciplina
          </h2>
          {medici.length === 0 ? (
            <p className="discipline-detail-empty">Nessun medico disponibile per questa disciplina al momento.</p>
          ) : (
            <div className="discipline-detail-grid" role="list">
              {medici.map((medico) => (
                <MedicoCard
                  key={medico.id}
                  medico={medico}
                  to={publicPath(`/medici/${getMedicoSlug(medico)}`)}
                />
              ))}
            </div>
          )}
        </section>

        <div className="discipline-detail-actions">
          <Link to={publicPath('/discipline')} className="vc-btn vc-btn-outline">Torna alle discipline</Link>
          <Link to={publicPath('/medici')} className="vc-btn vc-btn-primary">Vedi tutti i medici</Link>
        </div>
      </div>
    </div>
  );
};

export default DisciplineDetailPage;
